/* tslint:disable:max-file-line-count */

import * as angular from 'angular';

import { HippoApiService } from 'services/hippoApi';
import { AuthenticationService } from 'services/authentication';
import { AnalyticsService } from 'common/source/services/analytics';

import { getSortedCustomerPolicyInfos } from 'utilities/policyInfosHelper';
import { getMomentFromHippoDateString, getYearFromHippoDateString } from 'common/source/utilities/dates';
import * as _ from 'lodash';

export const module = angular.module('currentUserService', []);

export class CurrentUserService {
    private $q: angular.IQService;
    private $http: angular.IHttpService;
    private $window: angular.IWindowService;
    private hippoApiService: HippoApiService;
    private authenticationService: AuthenticationService;
    private policiesPromiseCache: angular.IPromise<Array<CustomerPolicyInfo>> | undefined;
    private policyIndexToPolicyInfoMap: Record<string, CustomerPolicyInfo>;
    private policyIdToPolicyIndexMap: Record<PolicyId, string>;
    private policyNumberToPolicyIndexMap: Record<string, string>;
    private policyUUIDToPolicyIndexMap: Record<string, string>;
    private analyticsService: AnalyticsService;

    constructor(
        $http: angular.IHttpService,
        hippoApiService: HippoApiService,
        authenticationService: AuthenticationService,
        analyticsService: AnalyticsService,
        $q: angular.IQService,
        $window: angular.IWindowService
    ) {
        this.$http = $http;
        this.hippoApiService = hippoApiService;
        this.authenticationService = authenticationService;
        this.analyticsService = analyticsService;
        this.$q = $q;
        this.$window = $window;
        this.policyIndexToPolicyInfoMap = {};
        this.policyIdToPolicyIndexMap = {};
        this.policyNumberToPolicyIndexMap = {};
        this.policyUUIDToPolicyIndexMap = {};
    }

    getPolicyIndexToPolicyInfoMap() {
        // For currentUser test
        return this.policyIndexToPolicyInfoMap;
    }

    getPolicyIdToPolicyIndexMap() {
        // For currentUser test
        return this.policyIdToPolicyIndexMap;
    }

    getPolicyNumberToPolicyIndexMap() {
        return this.policyNumberToPolicyIndexMap;
    }

    getPolicyUUIDToPolicyIndexMap() {
        return this.policyUUIDToPolicyIndexMap;
    }

    getPolicyCount() {
        return this.getDisplayOrderedCustomerPolicyInfos().then((policyInfos) => {
            return policyInfos.length;
        });
    }

    isLoggedIn() {
        return !!this.authenticationService.getAuthenticationToken();
    }

    firstName() {
        return this.getDisplayOrderedCustomerPolicyInfos().then((policyInfos) => {
            const latestBoundPolicy = _.maxBy(policyInfos, ({ bind_at }) =>
                getMomentFromHippoDateString(bind_at).unix()
            );
            return _.get(latestBoundPolicy, 'personal_information.first_name');
        });
    }

    lastName() {
        return this.getDisplayOrderedCustomerPolicyInfos().then((policyInfos) => {
            return policyInfos[0].personal_information.last_name;
        });
    }

    earliestPolicyYear() {
        return this.getDisplayOrderedCustomerPolicyInfos().then((policyInfos) =>
            _.min(policyInfos.map(({ effective_date }) => getYearFromHippoDateString(effective_date)))
        ) as angular.IPromise<string>;
    }

    getCustomerPolicyInfoByIndex(policyIndex: RoutePolicyIndex, useCache: boolean = true) {
        const self = this;
        return this.getDisplayOrderedCustomerPolicyInfos(useCache).then(() => {
            return self.policyIndexToPolicyInfoMap[policyIndex];
        });
    }

    getIndexedCustomerPolicyInfos(customerPolicyInfos: Array<CustomerPolicyInfo>) {
        if (_.isEmpty(this.policyIndexToPolicyInfoMap)) {
            throw new Error(
                'Must call getDisplayOrderedCustomerPolicyInfos before calling getIndexedCustomerPolicyInfos'
            );
        }
        return _.map(customerPolicyInfos, (policyInfo: CustomerPolicyInfo) => {
            const routeIndex = this.policyIdToPolicyIndexMap[policyInfo.id] as RoutePolicyIndex;
            return _.extend(_.cloneDeep(policyInfo), {
                $policyIndex: routeIndex
            });
        });
    }

    getDiscount(policyId: PolicyId, field: string): angular.IPromise<any> {
        return this.hippoApiService.calculateDiscount(policyId, field);
    }

    getUpdatedCustomerPolicyInfos(): angular.IPromise<Array<CustomerPolicyInfo>> {
        return this.getDisplayOrderedCustomerPolicyInfos(false);
    }

    clearCustomerPolicyInfosCache() {
        this.policiesPromiseCache = undefined;
    }

    getDisplayOrderedCustomerPolicyInfos(useCache: boolean = true): angular.IPromise<Array<CustomerPolicyInfo>> {
        const self = this;

        if (this.policiesPromiseCache && useCache) {
            return this.policiesPromiseCache;
        } else {
            const deferred = this.$q.defer();
            this.policiesPromiseCache = deferred.promise as angular.IPromise<Array<CustomerPolicyInfo>>;

            const hippoApiServicePromise = this.hippoApiService.getCustomerPolicyInfos();
            hippoApiServicePromise.then(
                function success(response) {
                    const policyInfos = response.data;
                    const sortedCustomerPolicyInfos = getSortedCustomerPolicyInfos(policyInfos);
                    const reversedCustomerPolicyInfos = sortedCustomerPolicyInfos.slice().reverse();
                    reversedCustomerPolicyInfos.forEach((policyInfo, index) => {
                        const routeIndex = String(reversedCustomerPolicyInfos.length - index) as RoutePolicyIndex;
                        self.policyIndexToPolicyInfoMap[routeIndex] = policyInfo;
                        self.policyIdToPolicyIndexMap[policyInfo.id] = routeIndex;
                        self.policyNumberToPolicyIndexMap[policyInfo.policy_number.toLowerCase()] = routeIndex;
                        self.policyUUIDToPolicyIndexMap[policyInfo.policy_uuid] = routeIndex;
                    });

                    self.analyticsService.identify(policyInfos[0].customer_id, {
                        email: policyInfos[0].personal_information.email
                    });
                    deferred.resolve(reversedCustomerPolicyInfos);
                },
                function error(response) {
                    // don't cache an error
                    self.policiesPromiseCache = undefined;

                    deferred.reject(response);
                }
            );

            return this.policiesPromiseCache;
        }
    }

    verifyAuthentication() {
        // we cast this type to return undefined so it is not used for purposes for which it is not intended
        return this.hippoApiService.verifyAuthentication();
    }

    getIntercomIdentityVerificationHash() {
        const queryData = {
            query: `query Customer{
                Customer{
                    id
                    intercomUserHash
                }
            }`
        };
        return this.$http<BffQueryResponse<CustomerOutput>>({
            method: 'POST',
            url: `${this.$window.appConfig.BFF_HOST}/graphql`,
            headers: {
                authorization: `Bearer ${this.authenticationService.getAuthenticationToken()}`,
                'x-hippo-client-platform': 'web'
            },
            data: queryData
        }).then((response) => {
            const { data, errors } = response.data;
            if (!data || (errors && errors.length > 0)) {
                throw new Error('error querying bff');
            }
            return data;
        });
    }
}

module.factory('currentUserService', function(
    $http: angular.IHttpService,
    authenticationService: AuthenticationService,
    hippoApiService: HippoApiService,
    analyticsService: AnalyticsService,
    $q: angular.IQService,
    $window: angular.IWindowService
) {
    'ngInject';

    return new CurrentUserService($http, hippoApiService, authenticationService, analyticsService, $q, $window);
});
